import { createContext, Dispatch, FC, ReactNode, useReducer } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  premium_member?: boolean;
  studio?: boolean;
  purchased_classes?: string[];
  [key: string]: any;
}

export interface AuthState {
  user: AuthUser | null;
  isAuthFlowCompleted: boolean;
  isAuthenticated: boolean;
  isStudioPassVip: boolean;
}

export enum AuthActionTypes {
  SET_USER = 'SET_USER',
  SET_AUTH_FLOW_COMPLETE = 'SET_AUTH_FLOW_COMPLETE',
  SET_IS_AUTHENTICATED = 'SET_IS_AUTHENTICATED',
  SET_STUDIO_PASS_VIP = 'SET_STUDIO_PASS_VIP',
  CLEAR_AUTH = 'CLEAR_AUTH',
}

type AuthAction =
  | { type: AuthActionTypes.SET_USER; payload: AuthUser | null }
  | { type: AuthActionTypes.SET_AUTH_FLOW_COMPLETE; payload: boolean }
  | { type: AuthActionTypes.SET_IS_AUTHENTICATED; payload: boolean }
  | { type: AuthActionTypes.SET_STUDIO_PASS_VIP; payload: boolean }
  | { type: AuthActionTypes.CLEAR_AUTH };

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

const initialState: AuthState = {
  user: null,
  isAuthFlowCompleted: false,
  isAuthenticated: false,
  isStudioPassVip: false,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case AuthActionTypes.SET_USER:
      return { ...state, user: action.payload };
    case AuthActionTypes.SET_AUTH_FLOW_COMPLETE:
      return { ...state, isAuthFlowCompleted: action.payload };
    case AuthActionTypes.SET_IS_AUTHENTICATED:
      return { ...state, isAuthenticated: action.payload };
    case AuthActionTypes.SET_STUDIO_PASS_VIP:
      return { ...state, isStudioPassVip: action.payload };
    case AuthActionTypes.CLEAR_AUTH:
      return { ...initialState };
    default:
      return state;
  }
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<{
  state: AuthState;
  dispatch: Dispatch<AuthAction>;
}>({ state: initialState, dispatch: () => null });

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  return (
    <AuthContext.Provider value={{ state, dispatch }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
